import Quill from 'quill';
import './QuillOverrides.js';
import Delta from 'quill-delta';
import { merge, cloneDeep } from 'lodash';

const urlRegex = /(https?:\/\/)?([-a-zA-Z0-9@:%_\+~#=]{1,256}\.)+[a-z]{2,6}([-a-zA-Z0-9@:%_\+.~#?!&//=]*)/g;

// Invariant: a link's target never comes from a fragment of the link's text. When a link's whole
// visible text is one URL, that URL is the target; otherwise its own href is.
//
// Synced event descriptions arrive as HTML, and an anchor's text can be split across text nodes
// (<wbr>, a nested <span>). Matching each node on its own gave the first piece a link to a
// truncated URL - clicking "https://zoom.us/j/465929" opened the wrong meeting while "0816"
// opened the right one.

const targetFor = url => (url.startsWith('http') ? url : `http://${url}`);

// The <a href> this text node sits in, looking no further than the clipboard container that
// Quill parses pasted and loaded HTML inside, so an editor mounted within a link is unaffected.
// An <a> without href is a bookmark (Outlook and Word emit these), not a link.
const enclosingLink = node => {
  for (let element = node.parentNode; element; element = element.parentNode) {
    if (element.classList && element.classList.contains('ql-clipboard')) return null;
    if (element.nodeName === 'A' && element.hasAttribute('href')) return element;
  }
  return null;
};

// The link's target when its entire visible text is a single URL, or null when the text is a
// label, several URLs, or a URL interrupted by a character outside the pattern.
const wholeTextTarget = link => {
  const text = link.textContent.trim();
  const matches = text.match(urlRegex);
  return matches && matches.length === 1 && matches[0] === text ? targetFor(text) : null;
};

export const linkHandler = function(range) {
  const text = this.quill.getText(0, range.index);
  let delta = new Delta();
  let match;
  let url;
  let prevOffset = 0;
  while ((match = urlRegex.exec(text)) !== null) {
    url = match[0];
    let target = url;
    if (!target.startsWith('http')) target = `http://${target}`;
    delta
      .retain(match.index - prevOffset)
      .delete(url.length)
      .insert(url, { link: target });
    prevOffset = match.index + url.length;
  }
  this.quill.updateContents(delta, Quill.sources.USER);
  prevOffset = range.index;
  return true;
};

export const linkMatcher = (node, delta) => {
  if (typeof node.data !== 'string') return;
  const link = enclosingLink(node);
  if (link) {
    // Every piece of a link whose text is a URL opens that whole URL, however the text is split.
    // Any other link is left alone, and Quill applies the anchor's own href to its text.
    const target = wholeTextTarget(link);
    return target ? new Delta().insert(node.data, { link: target }) : delta;
  }
  const matches = node.data.match(urlRegex);

  if (matches && matches.length > 0) {
    let str = node.data;
    let newDelta = new Delta();
    matches.forEach(function(match) {
      let split = str.split(match);
      let beforeLink = split.shift();
      let target = match;
      if (!target.startsWith('http')) target = `http://${target}`;
      newDelta.insert(beforeLink).insert(match, { link: target });
      str = split.join(match);
    });
    return newDelta.insert(str);
  }

  return delta;
};

// Opens the link a click landed in. Quill nests formatting inside links (<a><strong>0816</strong></a>),
// so the click target is often not the <a> itself; checking only the target left bold or italic
// parts of a link unclickable. Links outside the element handling the click are ignored, so an
// editor that happens to sit inside a link does not open it on every click.
export const openClickedLink = event => {
  const link = event.target.closest('a[href]');
  if (!link || !event.currentTarget.contains(link)) return false;
  window.open(link.href, '_blank');
  return true;
};

const defaults = {
  theme: 'snow',
  formats: [
    'background',
    'bold',
    'color',
    'font',
    'italic',
    'link',
    'size',
    'strike',
    'script',
    'underline',
    'blockquote',
    'header',
    'indent',
    'list',
    'align',
    'direction',
    'image',
    'video',
  ],
  modules: {
    toolbar: false,
    clipboard: {
      matchers: [[window.Node.TEXT_NODE, linkMatcher]],
      matchVisual: false,
    },
    keyboard: {
      bindings: {
        spaceToLink: {
          collapsed: true,
          key: ' ',
          handler: linkHandler,
        },
        enterToLink: {
          collapsed: true,
          key: 13,
          handler: linkHandler,
        },
        'header enter': {
          key: 13,
          collapsed: true,
          format: ['header'],
          suffix: /^$/,
          handler: function(range, context) {
            let [line, offset] = this.quill.getLine(range.index);
            let delta = new Delta()
              .retain(range.index)
              .insert('\n', context.format)
              .retain(line.length() - offset - 1);

            if (context.offset === 0) {
              delta = delta.retain(1);
            } else {
              delta = delta.retain(1, { header: null });
            }

            this.quill.updateContents(delta, Quill.sources.USER);
            this.quill.setSelection(range.index + 1, Quill.sources.SILENT);
            this.quill.scrollIntoView();
          },
        },
      },
    },
  },
};

export default (editor, config) => {
  const mergedOptions = merge(cloneDeep(defaults), config);
  if (config.hasOwnProperty('formats')) mergedOptions.formats = config.formats; // overwrite formats rather than using the merged version
  return new Quill(editor, mergedOptions);
};
